import { CommandType } from '@/types/websocket'
import { exists, SystemCode } from '@/api'
import { systemCodeStore } from '@/utils/store-accessor'

export const ReceiptPrintName = {
  ReceiptPrintLend: '貸出期限票',
  ReceiptPrintLendStatus: '貸出状況票',
  ReceiptPrintBibliography: '書誌資料情報',
  ReceiptPrintReserve: '予約連絡票',
  ReceiptPrintReserveForwarding: '予約回送票',
  ReceiptPrintReserveStatus: '予約状況票',
  ReceiptPrintShelf: '配架指示票',
  ReceiptPrintLost: '紛失届',
  ReceiptPrintReturnConfirm: '返却確認票',
  ReceiptPrintInterlibraryLoanBorrowReturnConfirm: '相互貸借資料返却確認票',
  ReceiptPrintMemo: '資料メモ票',
  ReceiptPrintAddressee: '宛名票',
  ReceiptPrintUserDueDateNoticeInfo: '更新手続きお知らせ',
  ReceiptPrintReceiveWaitingUser: '予約受取待ち利用者',
  ReceiptPrintCollectionStatusConfirm: '資料状態確認'
} as const
export type ReceiptPrintName = typeof ReceiptPrintName[keyof typeof ReceiptPrintName]

export interface ReceiptPrintHistory {
  commandType: CommandType
  receiptPrintName: ReceiptPrintName
  subject: string
  data: any
  dateTime: string
  maxPrintCount?: number
  customerCode?: string
}

export interface ReceiptPrintLostData {
  branchName: string
  collectionNo: string
  displayTitle: string
  lendData: string
  dueDate: string
  userId: string
  userName: string
}

export function ReceiptPrintLostDataToJSON(value: ReceiptPrintLostData): any {
  return {
    branch_name: value.branchName,
    collection_no: value.collectionNo,
    display_title: value.displayTitle,
    lend_date: value.lendData,
    due_date: value.dueDate,
    user_id: value.userId,
    user_name: value.userName
  }
}

export interface ReceiptPrintLendStatusData {
  title: string
  branchNamePrefix: string
  displayAuthor: string
  displayReserveCount: string
  dueDateLabel: string
  userNoMaskDigit: number
  totalLabel: string
  totalUnitLabel: string
  totalLabelFormatType: string
  notices: string[]
  collectionPartitionCount: number
  branchName: string
  userId: string
  collections: ReceiptPrintLendStatusCollectionData[]
  lendClassifications: SystemCode[]
}

export function ReceiptPrintLendStatusDataToJSON(
  value: ReceiptPrintLendStatusData
): any {
  return {
    title: value.title,
    branch_name_prefix: value.branchNamePrefix,
    display_author: value.displayAuthor,
    display_reserve_count: value.displayReserveCount,
    due_date_label: value.dueDateLabel,
    user_no_mask_digit: value.userNoMaskDigit,
    total_label: value.totalLabel,
    total_unit_label: value.totalUnitLabel,
    total_label_format_type: value.totalLabelFormatType,
    notices: value.notices,
    collection_partition_count: value.collectionPartitionCount,
    branch_name: value.branchName,
    user_id: value.userId,
    collections: value.collections.map(
      ReceiptPrintLendStatusCollectionDataToJSON
    ),
    lend_classifications: systemCodeStore.getClassification
  }
}

export interface ReceiptPrintLendStatusCollectionData {
  collectionNo: string
  displayTitle: string
  displayAuthor: string
  dueDate: string
  reserveCount: string
  lendClassification: string
}

function ReceiptPrintLendStatusCollectionDataToJSON(
  value: ReceiptPrintLendStatusCollectionData
): any {
  return {
    collection_no: value.collectionNo,
    display_title: value.displayTitle,
    display_author: value.displayAuthor,
    due_date: value.dueDate,
    reserve_count: value.reserveCount,
    lend_classification: value.lendClassification
  }
}

export interface ReceiptPrintReserveStatusData {
  customerCode: string
  branchName: string
  userId: string
  userNoMaskDigit: number
  collections: ReceiptPrintReserveStatusCollectionData[]
}

export function ReceiptPrintReserveStatusDataToJSON(
  value: ReceiptPrintReserveStatusData
): any {
  return {
    customer_code: value.customerCode,
    branch_name: value.branchName,
    user_id: value.userId,
    user_no_mask_digit: value.userNoMaskDigit,
    collections: value.collections.map(
      ReceiptPrintReserveStatusCollectionDataToJSON
    )
  }
}

export interface ReceiptPrintReserveStatusCollectionData {
  displayTitle: string
  no: string
  reserveDate: string
  receiveBranchName: string
  receivePlaceName: string
  reserveBranchName: string
  reserveExpiration: string
  message: string
  type: string
}

function ReceiptPrintReserveStatusCollectionDataToJSON(
  value: ReceiptPrintReserveStatusCollectionData
): any {
  return {
    display_title: value.displayTitle,
    no: value.no,
    reserve_date: value.reserveDate,
    receive_branch_name: value.receiveBranchName,
    receive_place_name: value.receivePlaceName,
    reserve_branch_name: value.reserveBranchName,
    reserve_expiration: value.reserveExpiration,
    message: value.message,
    type: value.type
  }
}

export interface ReceiptPrintReceiveWaitingUserData {
  userId: string
  branchName: string
}

export function ReceiptPrintReceiveWaitingUserDataToJSON(
  value: ReceiptPrintReceiveWaitingUserData
): any {
  return {
    user_id: value.userId,
    branch_name: value.branchName
  }
}

export interface ReceiptPrintReceiptCollectionStatusConfirmData {
  branchName: string
  lendUserReserveWaitingCollectionNo: string
  lendUserReserveWaitingForwardingBranchShortName: string
  lendUserReserveWaitingForwardingPlaceShortName: string
}

export const defaultReceiptPrintReceiptCollectionStatusConfirmData = (): ReceiptPrintReceiptCollectionStatusConfirmData => ({
  branchName: '',
  lendUserReserveWaitingCollectionNo: '',
  lendUserReserveWaitingForwardingBranchShortName: '',
  lendUserReserveWaitingForwardingPlaceShortName: ''
})

export function ReceiptCollectionStatusConfirmDataFromJSON(
  json: any
): ReceiptPrintReceiptCollectionStatusConfirmData {
  return {
    branchName: exists(json, 'branch_name') ? json.branch_name : '',
    lendUserReserveWaitingCollectionNo: exists(
      json,
      'lend_user_reserve_waiting_collection_no'
    )
      ? json.lend_user_reserve_waiting_collection_no
      : '',
    lendUserReserveWaitingForwardingBranchShortName: exists(
      json,
      'lend_user_reserve_waiting_forwarding_branch_short_name'
    )
      ? json.lend_user_reserve_waiting_forwarding_branch_short_name
      : '',
    lendUserReserveWaitingForwardingPlaceShortName: exists(
      json,
      'lend_user_reserve_waiting_forwarding_place_short_name'
    )
      ? json.lend_user_reserve_waiting_forwarding_place_short_name
      : ''
  }
}

export function ReceiptCollectionStatusConfirmToJSON(
  value: ReceiptPrintReceiptCollectionStatusConfirmData
): any {
  return {
    branch_name: value.branchName,
    lend_user_reserve_waiting_collection_no:
      value.lendUserReserveWaitingCollectionNo,
    lend_user_reserve_waiting_forwarding_branch_short_name:
      value.lendUserReserveWaitingForwardingBranchShortName,
    lend_user_reserve_waiting_forwarding_place_short_name:
      value.lendUserReserveWaitingForwardingPlaceShortName
  }
}
